import { getFieldTypeWidth, STANDARD_COLUMN_WIDTHS } from '../column-widths'
import { FieldTypeEnum } from '../custom-field.types'
import { Document, DocumentTypeEnum } from '../document'
import { ModuleColumnDefinition, ModuleTypeEnum } from '../module'
import { EntityTerminology } from '../organization-settings'

/**
 * Standard column definitions for the Document module
 * These correspond directly to the fields in the Document type
 *
 * @see Document type for the full data structure
 */
export const DOCUMENT_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'displayName',
    name: 'displayName' as keyof Document,
    label: 'Name',
    description: 'Name of the document',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: false,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '280px',
    minWidth: '200px',
  },
  {
    id: 'categoryId',
    name: 'categoryId' as keyof Document,
    label: 'Category',
    description: 'Document category',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 1,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '180px',
    minWidth: '150px',
  },
  {
    id: 'moduleInfo',
    name: 'moduleInfo' as keyof Document,
    label: 'Linked Record',
    description: 'Record this document is linked to',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.REFERENCE,
    width: '220px',
    minWidth: '180px',
  },
  {
    id: 'documentDate',
    name: 'documentDate' as keyof Document,
    label: 'Document Date',
    description: 'Date associated with the document',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.DATE,
    width: '140px',
    minWidth: '120px',
  },
  {
    id: 'type',
    name: 'type' as keyof Document,
    label: 'Type',
    description: 'Type of document (file or URL)',
    isVisible: false,
    isRequired: true,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 4,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '110px',
    minWidth: '90px',
  },
  {
    id: 'fileName',
    name: 'fileName' as keyof Document,
    label: 'File Name',
    description: 'Original file name',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 5,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '220px',
    minWidth: '180px',
  },
  {
    id: 'fileSize',
    name: 'fileSize' as keyof Document,
    label: 'Size',
    description: 'File size in bytes',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 6,
    fieldType: FieldTypeEnum.enum.NUMBER,
    width: '120px',
    minWidth: '100px',
  },
  {
    id: 'isPinned',
    name: 'isPinned' as keyof Document,
    label: 'Pinned',
    description: 'Pinned status',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 7,
    fieldType: FieldTypeEnum.enum.BOOLEAN,
    width: '110px',
    minWidth: '90px',
  },
  {
    id: 'tags',
    name: 'tags' as keyof Document,
    label: 'Tags',
    description: 'Document tags',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.TEXT,
    width: '180px',
    minWidth: '150px',
  },
  {
    id: 'entityGroup',
    name: 'moduleInfo' as keyof Document,
    label: 'Entity',
    description: 'Entity this document is linked to',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 9,
    fieldType: FieldTypeEnum.enum.REFERENCE,
    width: '140px',
    minWidth: '120px',
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof Document,
    label: 'Added',
    description: 'Date when document was added',
    isVisible: false,
    isRequired: true,
    isSystem: true,
    isFixed: false,
    order: 10,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '140px',
    minWidth: '120px',
  },
]

/**
 * Helper function to get all available columns for the document module,
 * combining standard and custom fields
 */
export function getDocumentColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>,
): ModuleColumnDefinition[] {
  // Start with standard columns
  const standardColumns = DOCUMENT_STANDARD_COLUMNS.map(column => ({
    ...column,
    // Override visibility if preferences exist
    isVisible:
      visibilityPreferences?.[column.id] !== undefined
        ? !!visibilityPreferences[column.id]
        : column.isVisible,
  }))

  // Default width for custom field columns based on field type
  const getCustomFieldWidth = (
    fieldType: string,
  ): { width: string; minWidth: string } => {
    switch (fieldType) {
      case FieldTypeEnum.enum.STRING:
        return { width: '180px', minWidth: '180px' }
      case FieldTypeEnum.enum.TEXT:
        return { width: '200px', minWidth: '200px' }
      case FieldTypeEnum.enum.NUMBER:
        return { width: '120px', minWidth: '120px' }
      case FieldTypeEnum.enum.BOOLEAN:
        return { width: '120px', minWidth: '120px' }
      case FieldTypeEnum.enum.DATE:
      case FieldTypeEnum.enum.DATETIME:
        return { width: '150px', minWidth: '150px' }
      case FieldTypeEnum.enum.DROPDOWN:
        return { width: '150px', minWidth: '150px' }
      default:
        return { width: '150px', minWidth: '150px' }
    }
  }

  // Map custom fields to column definitions
  const customColumns = customFields.map((field, index) => {
    const widthSettings = getCustomFieldWidth(field.fieldType)

    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible:
        visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
          ? !!visibilityPreferences[`custom_${field.fieldName}`]
          : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 91 + index, // Custom fields come after tags (order 90)
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
    }
  }) as ModuleColumnDefinition[]

  return [...standardColumns, ...customColumns]
}

/**
 * Get module type for column definitions
 */
export function getDocumentModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.DOCUMENT
}

/**
 * Standard document columns with the org's entity terminology applied to the
 * entity column label/description. Falls back to the default columns.
 */
export function createDocumentStandardColumns(
  terminology?: EntityTerminology,
): ModuleColumnDefinition[] {
  if (!terminology) return DOCUMENT_STANDARD_COLUMNS;
  const Singular = terminology.singular.charAt(0).toUpperCase() + terminology.singular.slice(1);
  return DOCUMENT_STANDARD_COLUMNS.map(column =>
    column.id === 'entityGroup'
      ? { ...column, label: Singular, description: `${Singular} this document is linked to` }
      : column,
  );
}
